/**
 * Every signal the car reports, as a live ticker.
 *
 * One row per channel: name, current value, an inline trace of the last N
 * samples, and the rate it is actually arriving at. Click a row to enlarge it,
 * or send it straight to a dashboard as a widget.
 *
 * Rows update through the render loop rather than React. At 0.1 s across a
 * couple of hundred channels, re-rendering the table would dominate the frame;
 * writing textContent and painting a 64px canvas does not.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ObdSession, SignalState } from '@obd/core';
import { INTERVAL_CHOICES, WIDGET_LABELS, type WidgetType } from '../store/appState';
import { theme, useCanvasPainter, useLiveText } from './render';
import { drawSeriesPanel, drawSparkline, formatValue } from './draw';
import { Modal } from './DashboardView';

export interface SignalsViewProps {
  session: ObdSession;
  structureVersion: number;
  defaultIntervalMs: number;
  onDefaultIntervalChange: (ms: number) => void;
  sparklinePoints: number;
  onSparklinePointsChange: (n: number) => void;
  /** Add this signal to the active dashboard as the given widget type. */
  onAddToDashboard: (signalKey: string, type: WidgetType) => void;
  dashboardName: string;
}

type SortKey = 'name' | 'value' | 'rate' | 'category';

export function SignalsView({
  session, structureVersion, defaultIntervalMs, onDefaultIntervalChange,
  sparklinePoints, onSparklinePointsChange, onAddToDashboard, dashboardName,
}: SignalsViewProps): JSX.Element {
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<string>('all');
  const [onlyLive, setOnlyLive] = useState(true);
  const [sort, setSort] = useState<SortKey>('category');
  const [detail, setDetail] = useState<string | null>(null);
  /** Per-signal interval overrides; everything else uses the global default. */
  const [overrides, setOverrides] = useState<Record<string, number>>({});

  const all = useMemo(() => {
    void structureVersion;
    return session.store.list();
  }, [session, structureVersion]);

  const categories = useMemo(
    () => ['all', ...[...new Set(all.map((s) => s.def.category))].sort()],
    [all],
  );

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = all.filter((s) => {
      if (onlyLive && !s.available) return false;
      if (category !== 'all' && s.def.category !== category) return false;
      if (!q) return true;
      return (
        s.def.name.toLowerCase().includes(q) ||
        s.def.short.toLowerCase().includes(q) ||
        s.def.key.includes(q)
      );
    });
    list = [...list];
    list.sort((a, b) => {
      switch (sort) {
        case 'name':
          return a.def.name.localeCompare(b.def.name);
        case 'value':
          return (b.value ?? -Infinity) - (a.value ?? -Infinity);
        case 'rate':
          return b.stats.hz - a.stats.hz;
        default:
          return a.def.category.localeCompare(b.def.category) || a.def.name.localeCompare(b.def.name);
      }
    });
    return list;
  }, [all, query, category, onlyLive, sort]);

  // Subscribing to what is on screen is what actually makes the rates real --
  // a signal nobody is looking at should not be costing bus bandwidth.
  useEffect(() => {
    const visible = rows.slice(0, 120);
    const entries: [string, number][] = visible.map((s) => [
      s.def.key,
      overrides[s.def.key] ?? defaultIntervalMs,
    ]);
    session.setSubscriptions(entries);
  }, [rows, session, defaultIntervalMs, overrides]);

  const setInterval = useCallback((key: string, ms: number) => {
    setOverrides((prev) => ({ ...prev, [key]: ms }));
  }, []);

  const detailState = detail ? session.store.get(detail) : undefined;

  return (
    <div className="signals">
      <div className="signals-toolbar">
        <input
          type="text"
          placeholder="Search…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="signals-search"
        />
        <select value={category} onChange={(e) => setCategory(e.target.value)}>
          {categories.map((c) => (
            <option key={c} value={c}>{c === 'all' ? 'All categories' : c}</option>
          ))}
        </select>
        <select value={sort} onChange={(e) => setSort(e.target.value as SortKey)}>
          <option value="category">Sort: category</option>
          <option value="name">Sort: name</option>
          <option value="value">Sort: value</option>
          <option value="rate">Sort: rate</option>
        </select>

        <div className="toolbar-group">
          <span className="muted">Update</span>
          <select value={defaultIntervalMs} onChange={(e) => onDefaultIntervalChange(Number(e.target.value))}>
            {INTERVAL_CHOICES.map((c) => (
              <option key={c.ms} value={c.ms}>{c.label}</option>
            ))}
          </select>
        </div>

        <div className="toolbar-group">
          <span className="muted">History</span>
          <select value={sparklinePoints} onChange={(e) => onSparklinePointsChange(Number(e.target.value))}>
            {[20, 40, 60, 120, 300].map((n) => (
              <option key={n} value={n}>{n} pts</option>
            ))}
          </select>
        </div>

        <label className="field-inline">
          <input type="checkbox" checked={onlyLive} onChange={(e) => setOnlyLive(e.target.checked)} />
          <span className="muted">Live only</span>
        </label>

        <span className="muted signals-count">{rows.length} signals</span>
      </div>

      <div className="signal-head">
        <span>Signal</span>
        <span className="right">Value</span>
        <span>Trend</span>
        <span className="right">Rate</span>
        <span>Poll</span>
        <span />
      </div>

      <div className="signal-rows">
        {rows.length === 0 && (
          <div className="empty">
            <strong>Nothing to show</strong>
            <span>
              {onlyLive
                ? 'No signals are reporting yet. Connect an adapter, or untick “Live only” to browse the full catalogue.'
                : 'No signals match this filter.'}
            </span>
          </div>
        )}
        {rows.map((state) => (
          <SignalRow
            key={state.def.key}
            state={state}
            points={sparklinePoints}
            intervalMs={overrides[state.def.key] ?? defaultIntervalMs}
            onIntervalChange={(ms) => setInterval(state.def.key, ms)}
            onOpen={() => setDetail(state.def.key)}
          />
        ))}
      </div>

      {detailState && (
        <SignalDetail
          state={detailState}
          onClose={() => setDetail(null)}
          onAdd={(type) => {
            onAddToDashboard(detailState.def.key, type);
            setDetail(null);
          }}
          dashboardName={dashboardName}
        />
      )}
    </div>
  );
}

// --------------------------------------------------------------------- row

interface SignalRowProps {
  state: SignalState;
  points: number;
  intervalMs: number;
  onIntervalChange: (ms: number) => void;
  onOpen: () => void;
}

function SignalRow({ state, points, intervalMs, onIntervalChange, onOpen }: SignalRowProps): JSX.Element {
  // Text and trace both refresh at the chosen interval, so "0.5 s" means the
  // number on screen changes twice a second, not sixty times.
  const valueRef = useLiveText<HTMLSpanElement>(
    () => formatValue(state.value, state.def),
    intervalMs,
  );
  const rateRef = useLiveText<HTMLSpanElement>(
    () => (state.stats.hz > 0 ? state.stats.hz.toFixed(1) + ' Hz' : '—'),
    1000,
  );

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      drawSparkline(ctx, state.history, 1, 2, w - 2, h - 4, {
        points,
        color: theme.get('--accent', '#3987e5'),
        fill: true,
      });
    },
    [state, points],
  );
  const sparkRef = useCanvasPainter(paint, intervalMs);

  return (
    <div className="signal-row" onClick={onOpen} role="button" tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter') onOpen(); }}>
      <span className="signal-name">
        <span className="signal-label">{state.def.name}</span>
        <span className="muted signal-cat">{state.def.category}</span>
      </span>
      <span className="signal-value tnum">
        <span ref={valueRef}>{formatValue(state.value, state.def)}</span>
        <span className="muted signal-unit">{state.def.unit}</span>
      </span>
      <canvas ref={sparkRef} className="signal-spark" />
      <span className="signal-rate tnum muted" ref={rateRef}>—</span>
      <select
        className="signal-interval"
        value={intervalMs}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onIntervalChange(Number(e.target.value))}
      >
        {INTERVAL_CHOICES.map((c) => (
          <option key={c.ms} value={c.ms}>{c.label}</option>
        ))}
      </select>
      <span className="signal-open muted">›</span>
    </div>
  );
}

// ------------------------------------------------------------------ detail

function SignalDetail({
  state, onClose, onAdd, dashboardName,
}: {
  state: SignalState;
  onClose: () => void;
  onAdd: (type: WidgetType) => void;
  dashboardName: string;
}): JSX.Element {
  const [windowS, setWindowS] = useState(120);

  const bigValue = useLiveText<HTMLSpanElement>(() => formatValue(state.value, state.def), 100);

  const paint = useCallback(
    (ctx: CanvasRenderingContext2D, w: number, h: number) => {
      const t1 = Date.now();
      const t0 = t1 - windowS * 1000;
      drawSeriesPanel(
        ctx, state,
        { x: 40, y: 8, w: Math.max(10, w - 48), h: Math.max(10, h - 26) },
        { t0, t1 },
        theme.get('--accent', '#3987e5'),
        { maxPoints: 4000 },
      );
      ctx.fillStyle = theme.get('--ink-muted', '#898781');
      ctx.font = "10px system-ui, -apple-system, 'Segoe UI', sans-serif";
      ctx.textBaseline = 'top';
      ctx.textAlign = 'left';
      ctx.fillText('-' + windowS + 's', 40, h - 14);
      ctx.textAlign = 'right';
      ctx.fillText('now', w - 8, h - 14);
    },
    [state, windowS],
  );
  const chartRef = useCanvasPainter(paint, 50);

  const s = state.stats;
  const hasStats = s.samples > 0;

  const offered: WidgetType[] = ['gauge', 'bar', 'number', 'line', 'light'];

  return (
    <Modal title={state.def.name} onClose={onClose}>
      <div className="detail-hero">
        <span className="detail-value tnum" ref={bigValue}>{formatValue(state.value, state.def)}</span>
        <span className="detail-unit muted">{state.def.unit}</span>
      </div>

      {state.def.description && <p className="muted small">{state.def.description}</p>}

      <div className="detail-chart">
        <canvas ref={chartRef} />
      </div>

      <div className="detail-controls">
        <label className="field-inline">
          <span className="muted">Window</span>
          <select value={windowS} onChange={(e) => setWindowS(Number(e.target.value))}>
            {[30, 60, 120, 300, 600, 1800].map((n) => (
              <option key={n} value={n}>{n < 60 ? n + ' s' : n / 60 + ' min'}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="detail-stats">
        <Stat label="Minimum" value={hasStats ? s.min.toFixed(state.def.decimals) : '—'} unit={state.def.unit} />
        <Stat label="Maximum" value={hasStats ? s.max.toFixed(state.def.decimals) : '—'} unit={state.def.unit} />
        <Stat label="Mean" value={hasStats ? s.mean.toFixed(state.def.decimals) : '—'} unit={state.def.unit} />
        <Stat label="Samples" value={String(s.samples)} unit="" />
        <Stat label="Rate" value={s.hz > 0 ? s.hz.toFixed(1) : '—'} unit="Hz" />
        <Stat label="Key" value={state.def.key} unit="" />
      </div>

      <div className="detail-add">
        <span className="muted">Add to “{dashboardName}” as:</span>
        <div className="detail-add-buttons">
          {offered.map((type) => (
            <button key={type} className="btn" data-size="sm" onClick={() => onAdd(type)}>
              {WIDGET_LABELS[type]}
            </button>
          ))}
        </div>
      </div>
    </Modal>
  );
}

function Stat({ label, value, unit }: { label: string; value: string; unit: string }): JSX.Element {
  return (
    <div className="stat">
      <span className="muted stat-label">{label}</span>
      <span className="stat-value tnum">
        {value}
        {unit && <span className="muted stat-unit"> {unit}</span>}
      </span>
    </div>
  );
}
